import React from 'react';

import { Modal, ModalHeader, ModalBody, ModalFooter } from 'Components/Modal';
import { PrimaryButton, SecondaryButton, DangerButton } from 'Components/Button';

import MeApi from 'Services/Api/Me/Me';

/**
 * Lets a confirmed in-person delegate either move to the virtual event or give up their
 * place entirely.
 *
 * Full cancellation stops all further event communications, so it asks a second time
 * before calling the API — the same two-step protection the browser dialogs gave us.
 *
 * @param {function} [onCancelled]  Called with (action, attendance_status) once the API confirms.
 * @param {function} onClose      Injected by the modal container.
 */
class CancelAttendanceModal extends React.Component {
    state = {
        confirmingFullCancel: false,
        working: null,
        error: null,
    };

    cancelAttendance = async (action) => {
        this.setState({ working: action, error: null });

        const response = await MeApi.cancelAttendance({ action });

        if (response?.status === 200) {
            this.props.onCancelled?.(action, response.data?.attendance_status);
            this.props.onClose();

            return;
        }

        this.setState({
            working: null,
            error: 'Something went wrong. Please try again.',
        });
    };

    handleSwitchToVirtual = () => this.cancelAttendance('switch_to_virtual');

    handleFullCancel = () => this.cancelAttendance('full_cancel');

    handleAskToConfirmFullCancel = () => this.setState({ confirmingFullCancel: true, error: null });

    handleBack = () => this.setState({ confirmingFullCancel: false, error: null });

    render () {
        const { confirmingFullCancel, working, error } = this.state;

        if (confirmingFullCancel) {
            return (
                <Modal size="max-w-lg">
                    <ModalHeader onClose={ this.props.onClose }>
                        Cancel your place completely?
                    </ModalHeader>

                    <ModalBody>
                        <div className="text-center space-y-4">
                            <p className="text-gray-700 text-base">
                                You will give up your place at Bupa Symposium 2026, in person and virtually,
                                and we will stop sending you communications about the event.
                            </p>

                            { error && (
                                <p className="text-red-500 text-sm">{ error }</p>
                            ) }
                        </div>
                    </ModalBody>

                    <ModalFooter alignment="center">
                        <SecondaryButton
                            onClick={ this.handleBack }
                            text="Go back"
                        />
                        <DangerButton
                            onClick={ this.handleFullCancel }
                            text="Yes, cancel my place"
                            working={ working === 'full_cancel' }
                        />
                    </ModalFooter>
                </Modal>
            );
        }

        return (
            <Modal size="max-w-lg">
                <ModalHeader onClose={ this.props.onClose }>
                    Cancel your in-person place?
                </ModalHeader>

                <ModalBody>
                    <div className="space-y-6">
                        <div className="space-y-2 text-center">
                            <p className="text-gray-700 text-base font-medium">Switch to virtual</p>
                            <p className="text-gray-700 text-sm">
                                Give up your seat at the venue but still join us online. You will keep
                                receiving event updates.
                            </p>
                            <PrimaryButton
                                onClick={ this.handleSwitchToVirtual }
                                text="Switch to virtual"
                                working={ working === 'switch_to_virtual' }
                            />
                        </div>

                        <div className="space-y-2 text-center border-t border-gray-100 pt-6">
                            <p className="text-gray-700 text-base font-medium">Cancel completely</p>
                            <p className="text-gray-700 text-sm">
                                Give up your place altogether. We will stop sending you communications
                                about the event.
                            </p>
                            <DangerButton
                                onClick={ this.handleAskToConfirmFullCancel }
                                text="Cancel completely"
                            />
                        </div>

                        { error && (
                            <p className="text-red-500 text-sm text-center">{ error }</p>
                        ) }
                    </div>
                </ModalBody>

                <ModalFooter alignment="center">
                    <SecondaryButton
                        onClick={ this.props.onClose }
                        text="Keep my in-person place"
                    />
                </ModalFooter>
            </Modal>
        );
    }
}

export default CancelAttendanceModal;
